import { connect } from "react-redux";
import { chargePointActions } from "../../store/actions/chargePoint.actions";
import { useParams } from "react-router-dom";
import { bindActionCreators } from "redux";
import { useSignalR } from "../../hooks/useSignalR";
import { useEffect, useState } from "react";
import LoaderComponent from "../../components/LoaderComponent";
import validationHelper from "../../utils/helpers/validation.helper";
import ChargePointChargingPriceElementComponent from "../../components/chargePoint/ChargePointChargingPriceElementComponent";
import ChargingTransactionDataComponent from "../../components/ChargingTransactions/ChargingTransactionDataComponent";
import { buttonElementModel } from "../../models/common/buttonElement.model";
import ButtonElementComponent from "../../components/common/ButtonElementComponent";
import { chargingTransctionActions } from "../../store/actions/transactions/chargingTransaction.actions";
import { chargingTransactioStatusEnum } from "../../enums/transactions/chargingTransactions/chargingTransactioStatusEnum";
import alertService from "../../services/alert/alert.service";
import { signalRKeysEnum } from "../../enums/signalR/signalRKeysEnum";
import driverSecurityErrorEnum from "../../enums/errors/transactions/driverSecurity/driverSecurityError.enum";

export const ChargingTransactionDetailsPage = ({ chargingTransactionReducer, chargePointActions, chargingTransctionActions }) => {
  const { chargePointId, transactionToken } = useParams();
  const { isConnected, lastMessage } = useSignalR(transactionToken);

  const [chargePoint, setChargePoint] = useState();
  const [transactionDetails, setTransactionDetails] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const [lastUpdateTime, setLastUpdateTime] = useState();
  // Spec 000306 - a link sent before the change opens the page read-only. Kept apart from transactionDetails,
  // which live updates replace.
  const [isReadOnlyLink, setIsReadOnlyLink] = useState(false);

  useEffect(() => {
    getChargingTransactionDetailsByToken();
  }, []); // Add dependencies here

  useEffect(() => {
    const fetchChargePointDetails = async () => {
      setIsLoading(true);
      const response = await chargePointActions.getChargePointDetails(chargePointId);
      if (validationHelper.validateHttpResponse(response)) {
        setChargePoint(response.payload);
      }
      setIsLoading(false);
    };

    fetchChargePointDetails();

    return () => {
      // todo: cancel active charge point events registration
    };
  }, [chargePointId, chargePointActions]); // Add dependencies here

  useEffect(() => {
    if (chargingTransactionReducer.chargingTransaction) {
      setTransactionDetails({ ...chargingTransactionReducer.chargingTransaction });
      setLastUpdateTime(Date.now());
    }
  }, [chargingTransactionReducer.chargingTransaction]);

  useEffect(() => {
    // Handle SignalR refresh messages explicitly
    if (lastMessage?.key === signalRKeysEnum.RefereshChargingTransacitonData) {
      console.log("Received transaction update via SignalR:", lastMessage.message);
      // Redux is already updated by useSignalR hook, component will re-render via Redux listener
    }
    
    switch (lastMessage?.key) {
      case signalRKeysEnum.StopChargingTransaction:
        setIsLoading(false);
        alertService.success("טעינת הרכב הסתיימה בהצלחה", "מידע על חיוב האשראי וקבלה יישלחו למייל איתו נרשמתם");
        break;
      default:
        break;
    }
  }, [lastMessage]);

  const getChargingTransactionDetailsByToken = async () => {
    setIsLoading(true);
    try {
      const response = await chargePointActions.getChargingTransactionDetailsByToken(transactionToken);
      if (validationHelper.validateHttpResponse(response) && response.payload) {
        setTransactionDetails(response.payload);
        setIsReadOnlyLink(response.payload.readOnly === true);
        setLastUpdateTime(Date.now());
        chargingTransctionActions.setChargingTransaction(response.payload);
      } else if (response?.errorCode === driverSecurityErrorEnum.LinkExpired) {
        alertService.error("הקישור פג תוקף", "קישור לטעינה פעיל עד 24 שעות אחרי סיום הטעינה.");
      } else {
        console.error("Failed to fetch transaction details:", response);
        alertService.error("שגיאה", "לא ניתן לטעון את פרטי הטעינה. אנא נסה שוב מאוחר יותר.");
      }
    } catch (error) {
      console.error("Error fetching transaction details:", error);
      alertService.error("שגיאה", "אירעה שגיאה בטעינת פרטי הטעינה. אנא נסה שוב מאוחר יותר.");
    } finally {
      setIsLoading(false);
    }
  };

  const getChargingTransactionDataComponent = () => {
    const chargingTransactionDataProps = { chargePoint, transactionDetails, lastUpdateTime };
    return <ChargingTransactionDataComponent props={chargingTransactionDataProps} />;
  };

  const handleStopChargingTransaction = async () => {
    setIsLoading(true);
    const response = await chargePointActions.stopChargingTransaction(transactionToken);
    if (!validationHelper.validateHttpResponse(response)) {
      setIsLoading(false);
      alertService.error(...getStopErrorMessage(response?.errorCode));
      return;
    }
    // alertService.info("סיום טרנזקציית טעינה", "אנא המתן לסיום הטעינה, תהליך זה עשוי להסתיים לאחר כ-2 דקות");
  };

  // Spec 000306 - stop works only with a current link, only while this session is the open one.
  const getStopErrorMessage = (errorCode) => {
    switch (errorCode) {
      case driverSecurityErrorEnum.SessionNotOpen:
        return ["הטעינה כבר הסתיימה", "לא בוצעה עצירה."];
      case driverSecurityErrorEnum.ReadOnlyLink:
        return ["קישור ישן", "ניתן לצפות בטעינה בלבד, לא לעצור אותה."];
      case driverSecurityErrorEnum.LinkExpired:
        return ["הקישור פג תוקף", "קישור לטעינה פעיל עד 24 שעות אחרי סיום הטעינה."];
      default:
        return ["שגיאה בסיום טרנזקציית טעינה", "אנה נסה שנית מאוחר יותר"];
    }
  };

  const getReadOnlyNoticeElement = () => {
    if (!isReadOnlyLink) return;
    return <div className="card">קישור ישן: ניתן לצפות בלבד</div>;
  };

  const getChargePointChargingPriceElement = () => {
    if (transactionDetails?.status !== chargingTransactioStatusEnum.Success) return;
    return <ChargePointChargingPriceElementComponent chargePoint={chargePoint} />;
  };

  const getStopChargingTransactionButtonElement = () => {
    if (isReadOnlyLink || transactionDetails?.status !== chargingTransactioStatusEnum.Success) return;

    const buttonElementProps = {
      ...buttonElementModel,
      label: "סיים טעינה",
      onClick: handleStopChargingTransaction,
      className: "marginTop10",
    };
    return <ButtonElementComponent buttonElementProps={buttonElementProps} />;
  };

  if (isLoading) return <LoaderComponent />;

  return (
    <div className="flexColumn gap20" style={{ maxWidth: "600px", margin: "0 auto", width: "100%" }}>
      {chargePoint && (
        <div className="card">
          {getChargingTransactionDataComponent()}
        </div>
      )}
      {chargePoint && getChargePointChargingPriceElement()}
      {getReadOnlyNoticeElement()}
      {getStopChargingTransactionButtonElement()}
    </div>
  );
};

const mapStateToProps = (state) => ({
  chargingTransactionReducer: state.chargingTransactionReducer,
});

const mapDispatchToProps = (dispatch) => {
  return {
    chargePointActions: bindActionCreators(chargePointActions, dispatch),
    chargingTransctionActions: bindActionCreators(chargingTransctionActions, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ChargingTransactionDetailsPage);
